import React, { useEffect, useState, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "react-router-dom";
import moment from "moment";
import useFetch from "utils/hooks/useFetch";
import Swal from "sweetalert2";
import {
  GET_TICKET_BY_ID,
  CREATE_BOOKING,
  CHECK_TICKET_STOCK,
  golf_pass_api,
  GET_POLO_JERSEY_SIZES,
  GET_GLOVE_SIZES,
} from "api";

import DateIcon from "pages/assets/icons/date";
import LocationIcon from "pages/assets/icons/location";

import NumberInput from "../components/QtyInput";
import ParticipantList from "../components/ParticipantList";

import validateForm from "../helpers/validateForm";
import formatParticipants from "../helpers/formatParticipants";

import { LoadingContext } from "components/layout/MainContainer";
import SuccessPayment from "../components/SuccessPayment";

// Images & Icons
import home_banner_desktop from "assets/images/home_banner_desktop.jpg";

function TicketDetails() {
  const { isLoading, setIsLoading } = useContext(LoadingContext);
  const [showModalSuccessRegister, setShowModalSuccessRegister] =
    useState(false);

  const { t } = useTranslation();
  const { id } = useParams();
  const location = useLocation();
  const { golfNGrow } = location.state || {};

  const { data: ticket, fetch: getTicket } = useFetch(GET_TICKET_BY_ID);
  const { data: ticketStock, fetch: checkTicketStock } =
    useFetch(CHECK_TICKET_STOCK);
  const {
    data: bookingResult,
    error: createBookingError,
    fetch: createBooking,
  } = useFetch(CREATE_BOOKING);
  const { error: getPoloJerseySizesError, fetch: getPoloJerseySizes } =
    useFetch(GET_POLO_JERSEY_SIZES, false, golf_pass_api);
  const { error: getGloveSizesError, fetch: getGloveSizes } = useFetch(
    GET_GLOVE_SIZES,
    false,
    golf_pass_api,
  );

  const [qty, setQty] = useState(0);
  const [formValues, setFormValues] = useState({});
  const [isRegistrationFormValid, setIsRegistrationFormValid] = useState(true);
  const [jerseySizeOptions, setJerseySizeOptions] = useState([]);
  const [formErrors, setFormErrors] = useState({});
  const [gloveSizeOptions, setGloveSizeOptions] = useState([]);

  useEffect(() => {
    getTicket({ params: { id } });
    checkTicketStock({
      data: { qty },
      params: { id },
    });
    getPoloJerseySizes().then((res) => {
      if (res && res.success) {
        const { poloJerseySizes } = res || {};
        let sizeOpts = poloJerseySizes.map((size) => {
          return { name: size.name, value: size.id, stock: size.stock };
        });
        setJerseySizeOptions(sizeOpts);
      }
    });
    getGloveSizes().then((res) => {
      if (res && res.success) {
        const { gloveSizes } = res || {};
        let sizeOpts = gloveSizes.map((size) => {
          return { name: size.name, value: size.id, stock: size.stock };
        });
        setGloveSizeOptions(sizeOpts);
      }
    });
  }, []);

  // Hardcoded: fixed qty for "scramble" ticket
  useEffect(() => {
    if (ticket && ticket.id) {
      if (ticket.is_group_ticket) {
        if (
          ticketStock &&
          ticketStock.stock &&
          parseInt(ticketStock.stock) >= 4
        ) {
          setQty(4);
        } else {
          setQty(0);
        }
      }
    }
  }, [ticket, ticketStock]);

  useEffect(() => {
    let errResp = null;
    if (createBookingError) {
      errResp =
        createBookingError &&
        createBookingError.data &&
        createBookingError.data.message;
    }
    if (getPoloJerseySizesError) {
      errResp =
        getPoloJerseySizesError &&
        getPoloJerseySizesError.data &&
        getPoloJerseySizesError.data.message;
    }
    if (getGloveSizesError) {
      errResp =
        getGloveSizesError &&
        getGloveSizesError.data &&
        getGloveSizesError.data.message;
    }
    if (errResp) {
      Swal.fire({
        title: "Error",
        text: errResp,
        icon: "error",
      }).then(() => {
        window.location.reload();
      });
    }
    if (bookingResult) {
      setIsLoading(false);
      setShowModalSuccessRegister(true);
    }
  }, [
    createBookingError,
    bookingResult,
    getPoloJerseySizesError,
    getGloveSizesError,
  ]);

  const validateRegistrationForm = () => {
    let requiredFields = ["customer-name", "customer-email"];

    if (!golfNGrow) {
      requiredFields = [
        ...requiredFields,
        "customer-phcode",
        "phone-number",
        "swing-speed",
        "preference",
        "gender",
        "jersey-size",
        "glove-size",
        "participant-category",
      ];
    }

    const result = validateForm(formValues, requiredFields);
    setFormErrors(result.errors);
    return result.isValid;
  };

  const handleSubmit = () => {
    if (validateRegistrationForm()) {
      setIsLoading(true);

      let participants = formatParticipants(formValues, qty).map(
        (participant) => {
          const matchJersey = jerseySizeOptions.find(
            (opt) => opt.value === +participant.jersey_size,
          );

          const matchGlove = gloveSizeOptions.find(
            (opt) => opt.value === +participant.glove_size,
          );

          return {
            ...participant,
            jersey_size_id: participant.jersey_size,
            jersey_size: matchJersey?.name || "",
            glove_size_id: participant.glove_size,
            glove_size: matchGlove?.name || "",
            participant_category:
              participant.participant_category !== "Topgolf" &&
              participant.participant_category !== "Mandiri"
                ? participant.participant_category_other
                : participant.participant_category,
          };
        },
      );

      let body = {
        ticket_id: id,
        participants,
      };

      createBooking({ data: { ...body } });
    } else {
      setIsRegistrationFormValid(false);
    }
  };

  return (
    <div>
      {showModalSuccessRegister ? (
        <SuccessPayment />
      ) : (
        <div>
          <section>
            <div className="relative">
              {/* home banner desktop size */}
              <img
                // src="https://storage.googleapis.com/testing_storage_cloudxier/cloudxier-images/1c0610da06e3edba525e31bbf4bf526fa276d28803c039a64992d024874448b71b02826c5169acbf1c7c61b0dd764488a8a0bf8cd98872c2474650c847a0ffe234f1c22d82f685685cc5bfa38d5bd67b3cfa88080b46a965d68d693de5546d5439ef6d2d5800e97258a00974dc45dfebf67e847305ed821e943f/file-1736775970398.jpg"
                src={home_banner_desktop}
                alt="main"
                className="object-cover object-center w-full max-h-40 sm:max-h-60"
              />
              <div className="absolute top-0 bottom-0 left-0 right-0 text-white bg-black/60">
                <div className="flex flex-col items-center justify-center h-full gap-4 app-main-container sm:gap-8">
                  <p className="max-w-md px-8 text-lg font-semibold text-center sm:text-3xl sm:font-bold app-animation-translate-start">
                    {t("ticketDetails")}
                  </p>
                </div>
              </div>
            </div>
          </section>
          <section>
            <div className="space-y-10 app-main-container md:space-y-12">
              <div className="text-xl md:text-3xl">
                {ticket && ticket.title}
              </div>
              <div className="space-y-4 md:space-y-6">
                <div className="flex gap-4">
                  <div>
                    <DateIcon bgFill={"#0B1B3C"} fill={"#0B1B3C"} size={50} />
                  </div>
                  <div className="space-y-0.5">
                    <div className="text-base md:text-xl">
                      {ticket && moment(ticket.date).format("DD MMMM YYYY")}
                    </div>
                    <div className="text-sm font-light md:text-base">
                      {ticket && moment(ticket.date).format("dddd, hh:mma")}
                    </div>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div>
                    <LocationIcon
                      bgFill={"#0B1B3C"}
                      fill={"#0B1B3C"}
                      size={50}
                    />
                  </div>
                  <div className="space-y-0.5">
                    <div className="text-base md:text-xl">
                      {ticket && ticket.address_title}
                    </div>
                    <div className="text-sm font-light md:text-base">
                      {ticket && ticket.address_details}
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <div className="mb-4 text-base font-bold md:text-xl">
                  {t("ticketDescription")}
                </div>
                <div
                  className="text-base md:text-xl custom-list"
                  dangerouslySetInnerHTML={{
                    __html: ticket && ticket.description,
                  }}
                ></div>
              </div>
              <div>
                <div className="mb-4 text-base font-bold md:text-xl">
                  {t("qty")}
                </div>
                <NumberInput
                  setQty={setQty}
                  qty={qty}
                  maxQty={
                    ticketStock &&
                    ticketStock.stock &&
                    parseInt(ticketStock.stock)
                  }
                  disabled={
                    (ticket && ticket.is_group_ticket) ||
                    (ticketStock &&
                      ticketStock.stock &&
                      parseInt(ticketStock.stock) < 1)
                  }
                />
                {(ticket &&
                  !ticket.is_group_ticket &&
                  ticketStock &&
                  ticketStock.stock &&
                  parseInt(ticketStock.stock) > 0) ||
                (ticket &&
                  ticket.is_group_ticket &&
                  ticketStock &&
                  ticketStock.stock &&
                  parseInt(ticketStock.stock) > 3) ? (
                  // <div className='pt-2 text-xs font-bold md:text-sm text-app-blue'>{ticketStock && ticketStock.stock || 0} {t('availableStock')}</div>
                  <></>
                ) : (
                  <div className="pt-2 text-xs font-bold md:text-sm text-amber-700">
                    {t("unavailableStock")}
                  </div>
                )}
              </div>
              {(qty && qty > 0 && (
                <div>
                  <div className="mb-4 text-base font-bold md:text-xl">
                    {t("participants")}
                  </div>
                  <ParticipantList
                    formValues={formValues}
                    setFormValues={setFormValues}
                    qty={qty}
                    isRegistrationFormValid={isRegistrationFormValid}
                    setIsRegistrationFormValid={setIsRegistrationFormValid}
                    genderOptions={[
                      { name: "Male", value: "Male" },
                      { name: "Female", value: "Female" },
                    ]}
                    jerseySizeOptions={jerseySizeOptions}
                    formErrors={formErrors}
                    gloveSizeOptions={gloveSizeOptions}
                    swingSpeedOptions={[
                      { name: "Under 105mph", value: "Under 105mph" },
                      { name: "Over 105mph", value: "Over 105mph" },
                    ]}
                    preferenceOptions={[
                      { name: "More distance", value: "More distance" },
                      { name: "More spin", value: "More spin" },
                    ]}
                    participantCategoryOptions={[
                      { name: "Topgolf", value: "Topgolf" },
                      { name: "Mandiri", value: "Mandiri" },
                      { name: t("otherOption"), value: t("otherOption") },
                    ]}
                  />
                </div>
              )) ||
                null}
              {(qty && qty > 0 && (
                <div className="flex justify-center md:justify-end">
                  <button
                    onClick={handleSubmit}
                    className="px-12 py-2 text-sm text-white rounded-md bg-app-blue sm:text-base disabled:opacity-50"
                  >
                    {t("submitRegistration")}
                  </button>
                </div>
              )) ||
                null}
            </div>
          </section>
        </div>
      )}
    </div>
  );
}

export default TicketDetails;
